'use client';

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Button,
  Tabs,
  Table,
  Badge,
  Alert,
  Space,
  Descriptions,
} from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  CodeOutlined,
  CloudUploadOutlined,
  HistoryOutlined,
  RocketOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { getInstalledModules, getActiveTier } from '@/lib/modules/registry';
import type { ModuleManifest, PatchHistoryEntry } from '@/lib/modules/types';

const { Title, Text, Paragraph } = Typography;

export default function AdminModulesPage() {
  const [modules, setModules] = useState<ModuleManifest[]>([]);
  const [activeTier, setActiveTier] = useState<number>(1);
  const [history, setHistory] = useState<PatchHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setModules(getInstalledModules());
    setActiveTier(getActiveTier());

    // Load patch history
    async function fetchHistory() {
      try {
        setLoading(true);
        const res = await fetch('/api/admin/modules/history');
        if (res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data)) {
            setHistory(json.data);
          }
        }
      } catch (err) {
        console.error('Failed to load patch history:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, []);

  const tierDefinitions = [
    {
      tier: 1,
      code: 'TIER_1_PROCUREMENT',
      title: 'Paket 1: Procurement, Gudang & Keuangan AP',
      tagColor: 'green',
      status: 'AKTIF & TERPASANG',
      priceDesc: 'Paket Dasar (Core)',
      features: [
        'Purchase Request (PR) & Line-Item Approval Owner',
        'Konsolidasi Multi-PR ke 1 Purchase Order (PO)',
        'Penerimaan Gudang (GR) & Zero Over-delivery Prevention',
        'Atomic FIFO Cost Layers & Valuasi Persediaan Otomatis',
        'Finance 3-Way Match & Pembayaran Cicilan Bertahap (Termin)',
        'Approval Manager & Payment Voucher Konsolidasi Multi-PO',
      ],
      description:
        'Pondasi operasional pengadaan barang baku, logistik gudang, dan pengendalian kas keluar vendor.',
    },
    {
      tier: 2,
      code: 'TIER_2_MAKLOON',
      title: 'Paket 2: Modul Makloon & Produksi CMT',
      tagColor: activeTier >= 2 ? 'green' : 'blue',
      status: activeTier >= 2 ? 'AKTIF & TERPASANG' : 'UPGRADE TERSEDIA (SIAP DI-PATCH)',
      priceDesc: 'Paket 1 + Modul Makloon',
      features: [
        'Order Makloon / CMT (Cut, Make, Trim) ke Vendor Rekanan',
        'Surat Perintah Kerja (SPK) Potong, Sablon, Bordir, Sewing',
        'Bill of Materials (BOM) & Resep Pemakaian Bahan Baku Makloon',
        'Quality Inspection Hasil Jahit & Tracking Barang Cacat Vendor',
        'Rekonsiliasi Pemakaian Kain vs Hasil Jadi Garmen',
      ],
      description:
        'Manajemen rantai produksi makloon garmen eksternal dengan pengawasan bahan baku dan hasil potong.',
    },
    {
      tier: 3,
      code: 'TIER_3_DISTRIBUTION',
      title: 'Paket 3: Modul Distribusi & Logistik Produk',
      tagColor: activeTier >= 3 ? 'green' : 'purple',
      status: activeTier >= 3 ? 'AKTIF & TERPASANG' : 'TERSEDIA DI PAKET 3',
      priceDesc: 'Paket 1 + Paket 2 + Modul Distribusi',
      features: [
        'Sales Orders (SO) & Alokasi Stok Barang Jadi',
        'Surat Jalan Pengiriman Resmi ke Pelanggan / Distributor',
        'Packing List Ekspedisi & Muatan Kontainer / Truk',
        'Tracking Resi Ekspedisi & Status Tiba di Tujuan',
        'Integrasi Piutang Penjualan (Accounts Receivable)',
      ],
      description:
        'Ekosistem hilir untuk mendistribusikan produk jadi ke jaringan agen, distributor, dan retail.',
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 60 }}>
      {/* Header Banner */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 4 }}>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: '#6366F1',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              background: '#EEF2FF',
              padding: '2px 8px',
              borderRadius: 4,
            }}
          >
            Arsitektur Modular &amp; Addon Registry (Opsi 2A)
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>
              Paket Lisensi &amp; Manajemen Patch Modul
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Sistem emakloon dirancang dengan arsitektur patch modular independen. Source code per paket terisolasi dan dapat di-upgrade secara aman via CLI patch runner.
            </Text>
          </div>

          <div
            style={{
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              padding: '6px 16px',
              borderRadius: 30,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Badge status="processing" color="#6366F1" />
            <Text strong style={{ fontSize: 13, color: '#1E293B' }}>
              Tier Aktif: <Tag color="indigo" style={{ fontWeight: 700, margin: 0 }}>Paket {activeTier}</Tag>
            </Text>
          </div>
        </div>
      </div>

      <Tabs
        defaultActiveKey="packages"
        items={[
          {
            key: 'packages',
            label: (
              <span>
                <AppstoreOutlined /> Paket Penjualan &amp; Addon ({tierDefinitions.length})
              </span>
            ),
            children: (
              <div>
                <Row gutter={[16, 16]}>
                  {tierDefinitions.map((def) => {
                    const isInstalled = activeTier >= def.tier;
                    return (
                      <Col xs={24} lg={8} key={def.tier}>
                        <Card
                          variant="borderless"
                          style={{
                            borderRadius: 16,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            border: isInstalled ? '2px solid #6366F1' : '1px solid #E2E8F0',
                            background: isInstalled ? '#FFFFFF' : '#F8FAFC',
                            boxShadow: isInstalled ? '0 10px 25px -5px rgba(99, 102, 241, 0.1)' : 'none',
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                              <Tag
                                color={isInstalled ? 'green' : 'default'}
                                style={{ fontWeight: 700, borderRadius: 6, padding: '2px 8px', fontSize: 11 }}
                              >
                                {def.status}
                              </Tag>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748B' }}>
                                TIER {def.tier}
                              </span>
                            </div>

                            <Title level={4} style={{ margin: 0, fontWeight: 800, color: '#0F172A', fontSize: 16 }}>
                              {def.title}
                            </Title>
                            <div style={{ fontSize: 11.5, color: '#6366F1', fontWeight: 600, marginTop: 2 }}>
                              {def.priceDesc}
                            </div>

                            <Paragraph style={{ fontSize: 12, color: '#64748B', marginTop: 10, lineHeight: 1.5 }}>
                              {def.description}
                            </Paragraph>

                            <div style={{ borderTop: '1px solid #F1F5F9', paddingTop: 12, marginTop: 12 }}>
                              <Text strong style={{ fontSize: 12, color: '#334155', display: 'block', marginBottom: 8 }}>
                                Cakupan Fitur Modul:
                              </Text>
                              <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                                {def.features.map((f, i) => (
                                  <li key={i}>{f}</li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid #F1F5F9' }}>
                            {isInstalled ? (
                              <Button
                                block
                                icon={<CheckCircleOutlined />}
                                style={{
                                  background: '#F0FDF4',
                                  borderColor: '#BBF7D0',
                                  color: '#16A34A',
                                  fontWeight: 700,
                                  borderRadius: 8,
                                }}
                              >
                                Modul Terpasang &amp; Beroperasi
                              </Button>
                            ) : (
                              <Button
                                block
                                icon={<RocketOutlined />}
                                type="primary"
                                style={{
                                  background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                                  borderColor: '#4F46E5',
                                  fontWeight: 700,
                                  borderRadius: 8,
                                }}
                                onClick={() => {
                                  const cmd = `npx tsx scripts/patch-manager/cli.ts apply sample-patches/paket-${def.tier}/`;
                                  navigator.clipboard?.writeText(cmd);
                                  alert(`Jalankan perintah berikut di terminal server untuk menerapkan patch Paket ${def.tier}:\n\n${cmd}`);
                                }}
                              >
                                Terapkan Patch Paket {def.tier} ➔
                              </Button>
                            )}
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            ),
          },
          {
            key: 'patch-cli',
            label: (
              <span>
                <CodeOutlined /> Mekanisme Zero-Error Patching
              </span>
            ),
            children: (
              <Card variant="borderless" style={{ borderRadius: 16, border: '1px solid #E2E8F0' }}>
                <Title level={4} style={{ fontWeight: 800, color: '#0F172A' }}>
                  Standar Eksekusi Patch Anti-Gagal (Zero-Error Invariants)
                </Title>
                <Paragraph style={{ fontSize: 13, color: '#475569' }}>
                  Setiap upgrade paket dieksekusi secara atomik menggunakan CLI patch runner di server. Sistem menjamin tidak ada kerusakan data transaksi yang sudah berjalan melalui 6 lapisan proteksi:
                </Paragraph>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col xs={24} md={12}>
                    <div style={{ background: '#F8FAFC', padding: 16, borderRadius: 12, border: '1px solid #E2E8F0', height: '100%' }}>
                      <Text strong style={{ fontSize: 13, color: '#0F172A' }}>
                        1. Pre-flight &amp; Checksum Verification
                      </Text>
                      <p style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                        Memverifikasi konektivitas MariaDB, memastikan dependensi paket sebelumnya terpenuhi (Paket 2 mewajibkan Paket 1), dan mencocokkan SHA256 checksum bundle.
                      </p>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ background: '#F8FAFC', padding: 16, borderRadius: 12, border: '1px solid #E2E8F0', height: '100%' }}>
                      <Text strong style={{ fontSize: 13, color: '#0F172A' }}>
                        2. Transactional Backup Snapshot
                      </Text>
                      <p style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                        Sebelum file diubah, CLI membuat backup instan seluruh database MariaDB (`mysqldump`) dan snapshot direktori source code ke folder `.patch-backup/`.
                      </p>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ background: '#F8FAFC', padding: 16, borderRadius: 12, border: '1px solid #E2E8F0', height: '100%' }}>
                      <Text strong style={{ fontSize: 13, color: '#0F172A' }}>
                        3. Additive Schema Migration (Prisma Folder)
                      </Text>
                      <p style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                        Tabel modul baru di-inject ke <code>prisma/schema/[module].prisma</code> dan dieksekusi dengan <code>prisma db push --accept-data-loss=false</code> (mutlak anti hapus kolom lama).
                      </p>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ background: '#F8FAFC', padding: 16, borderRadius: 12, border: '1px solid #E2E8F0', height: '100%' }}>
                      <Text strong style={{ fontSize: 13, color: '#0F172A' }}>
                        4. Automatic Rollback on Compilation Error
                      </Text>
                      <p style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                        Menjalankan `tsc --noEmit`. Jika ditemukan satu saja error kompilasi, sistem otomatis membatalkan seluruh perubahan dan merestore database &amp; file ke kondisi semula.
                      </p>
                    </div>
                  </Col>
                </Row>

                <div style={{ marginTop: 24, background: '#0F172A', padding: 18, borderRadius: 12, color: '#F8FAFC' }}>
                  <Text strong style={{ color: '#A5B4FC', display: 'block', marginBottom: 8, fontSize: 12 }}>
                    CONTOH PERINTAH CLI DI TERMINAL SERVER:
                  </Text>
                  <code style={{ color: '#38BDF8', fontSize: 13, display: 'block' }}>
                    # 1. Cek status paket terpasang
                    <br />
                    npx tsx scripts/patch-manager/cli.ts list
                    <br />
                    <br />
                    # 2. Terapkan bundle patch Paket 2 (Makloon)
                    <br />
                    npx tsx scripts/patch-manager/cli.ts apply /path/to/paket-2-makloon.emp
                    <br />
                    <br />
                    # 3. Rollback jika diperlukan
                    <br />
                    npx tsx scripts/patch-manager/cli.ts rollback
                  </code>
                </div>
              </Card>
            ),
          },
          {
            key: 'history',
            label: (
              <span>
                <HistoryOutlined /> Riwayat Patch &amp; Audit Log
              </span>
            ),
            children: (
              <Card variant="borderless" style={{ borderRadius: 16, border: '1px solid #E2E8F0' }}>
                <Table
                  rowKey="patchId"
                  loading={loading}
                  dataSource={history}
                  scroll={{ x: 800 }}
                  pagination={false}
                  columns={[
                    {
                      title: 'ID Patch',
                      dataIndex: 'patchId',
                      key: 'patchId',
                      render: (t) => <strong>{t}</strong>,
                    },
                    {
                      title: 'Modul / Paket',
                      key: 'module',
                      render: (_, r) => (
                        <div>
                          <div style={{ fontWeight: 600 }}>{r.moduleName}</div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>
                            Tier {r.tier} • v{r.version}
                          </div>
                        </div>
                      ),
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      render: (s) => (
                        <Tag color={s === 'SUCCESS' ? 'green' : s === 'ROLLED_BACK' ? 'orange' : 'red'}>
                          {s}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Waktu Instalasi',
                      dataIndex: 'installedAt',
                      key: 'installedAt',
                      render: (t) => (t ? new Date(t).toLocaleString('id-ID') : '-'),
                    },
                    {
                      title: 'Operator',
                      dataIndex: 'installedBy',
                      key: 'installedBy',
                    },
                    {
                      title: 'Checksum SHA256',
                      dataIndex: 'checksum',
                      key: 'checksum',
                      render: (c) => <code style={{ fontSize: 11 }}>{c ? c.slice(0, 16) + '...' : '-'}</code>,
                    },
                  ]}
                  locale={{
                    emptyText: (
                      <div style={{ padding: '30px 0', textAlign: 'center', color: '#94A3B8' }}>
                        Belum ada patch eksternal yang diaplikasikan. Sistem berjalan pada Paket 1 Dasar.
                      </div>
                    ),
                  }}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}
